// src/components/Admin/MultiGalleryUpload.jsx
import React, { useState } from 'react';
import { galleryService } from '../../services/galleryService';
import './MultiGalleryUpload.css'; // Import the CSS

const MultiGalleryUpload = ({ categories = [], onComplete }) => {
    const [files, setFiles] = useState([]);
    const [mediaItems, setMediaItems] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState([]);

    const handleFileSelect = (e) => {
        const selected = Array.from(e.target.files);
        if (selected.length === 0) return;
        if (selected.length > 20) {
            alert('Maximum 20 files per batch');
            return;
        }

        const validFiles = [];
        const invalidFiles = [];
        selected.forEach(file => {
            const ext = file.name.split('.').pop().toLowerCase();
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            const isVideo = ['mp4', 'webm', 'mov'].includes(ext);
            if ((isImage || isVideo) && file.size <= (isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024)) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name);
            }
        });

        if (invalidFiles.length) {
            alert(`Invalid files (unsupported or too large):\n${invalidFiles.join('\n')}`);
        }

        const newMediaItems = validFiles.map(file => ({
            file,
            title: file.name.replace(/\.[^/.]+$/, ""),
            description: '',
            category: '',
            tags: '',
            featured: false,
            media_type: file.type.startsWith('video/') ? 'video' : 'image',
            file_url: null,
            thumbnail_url: null,
            uploadStatus: 'pending'
        }));

        setFiles(prev => [...prev, ...validFiles]);
        setMediaItems(prev => [...prev, ...newMediaItems]);
    };

    const updateItem = (index, field, value) => {
        const updated = [...mediaItems];
        updated[index][field] = value;
        setMediaItems(updated);
    };

    const removeItem = (index) => {
        const newFiles = [...files];
        const newItems = [...mediaItems];
        newFiles.splice(index, 1);
        newItems.splice(index, 1);
        setFiles(newFiles);
        setMediaItems(newItems);
    };

    const handleSubmit = async () => {
        if (mediaItems.length === 0) {
            alert('No files to upload');
            return;
        }

        for (let i = 0; i < mediaItems.length; i++) {
            if (!mediaItems[i].title.trim()) {
                alert(`Item ${i + 1} is missing a title`);
                return;
            }
        }

        setUploading(true);
        setErrors([]);

        // Step 1: Upload each file
        for (let i = 0; i < mediaItems.length; i++) {
            const item = mediaItems[i];
            if (item.file_url) continue;

            try {
                item.uploadStatus = 'uploading';
                setMediaItems([...mediaItems]);
                
                const response = await galleryService.uploadFile(item.file);
                if (response.success) {
                    item.file_url = response.file_url;
                    item.thumbnail_url = response.thumbnail_url || response.file_url;
                    item.uploadStatus = 'done';
                } else {
                    throw new Error(response.message || 'Upload failed');
                }
            } catch (err) {
                item.uploadStatus = 'error';
                setErrors(prev => [...prev, `File "${item.file.name}": ${err.message}`]);
            }
            setMediaItems([...mediaItems]);
        }

        // Step 2: Batch create in DB
        const successItems = mediaItems.filter(item => item.file_url && item.uploadStatus === 'done');
        if (successItems.length === 0) {
            setUploading(false);
            alert('No files were uploaded successfully');
            return;
        }

        const payload = successItems.map(item => ({
            media_type: item.media_type,
            title: item.title,
            description: item.description,
            file_url: item.file_url,
            thumbnail_url: item.thumbnail_url,
            category: item.category,
            tags: item.tags,
            featured: item.featured ? 1 : 0,
            order_position: 0
        }));

        try {
            const result = await galleryService.adminBatchCreateMedia(payload);
            if (result.success) {
                alert(`Successfully uploaded ${result.inserted_ids?.length || successItems.length} items!`);
                if (onComplete) onComplete(result);
                // Reset form
                setFiles([]);
                setMediaItems([]);
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            alert('Batch create failed: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const getStatusText = (status) => {
        switch(status) {
            case 'pending': return 'Pending';
            case 'uploading': return 'Uploading...';
            case 'done': return 'Uploaded';
            case 'error': return 'Failed';
            default: return 'Pending';
        }
    };

    const getStatusClass = (status) => {
        switch(status) {
            case 'pending': return 'pending';
            case 'uploading': return 'uploading';
            case 'done': return 'done';
            case 'error': return 'error';
            default: return 'pending';
        }
    };

    return (
        <div className="batch-upload-modern">
            <div className="batch-upload-header">
                <h3>
                    <i className="fas fa-images"></i>
                    Batch Upload
                    <span className="batch-badge">Multiple Files</span>
                </h3>
            </div>

            {/* Upload Zone */}
            <div className="upload-zone-modern">
                <div className="upload-area-modern" onClick={() => !uploading && document.getElementById('batch-file-input').click()}>
                    <i className="fas fa-cloud-upload-alt"></i>
                    <p>Click to select multiple files</p>
                    <small>Supports: JPG, PNG, GIF, WebP, MP4, WebM, MOV (Max 20 files)</small>
                    <input
                        id="batch-file-input"
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleFileSelect}
                        disabled={uploading}
                        style={{ display: 'none' }}
                    />
                </div>
                <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
                    <i className="fas fa-info-circle"></i> Max 10MB per image, 100MB per video
                </small>
            </div>

            {/* Media Items List */}
            {mediaItems.length > 0 && (
                <div className="media-items-list-modern">
                    {mediaItems.map((item, idx) => (
                        <div key={idx} className="media-item-card">
                            {/* Preview */}
                            <div className="media-preview">
                                {item.file && item.file.type.startsWith('video/') ? (
                                    <video src={URL.createObjectURL(item.file)} />
                                ) : (
                                    <img src={URL.createObjectURL(item.file)} alt="preview" />
                                )}
                            </div>

                            {/* Fields */}
                            <div className="media-fields">
                                <input
                                    type="text"
                                    placeholder="Title *"
                                    value={item.title}
                                    onChange={(e) => updateItem(idx, 'title', e.target.value)}
                                    disabled={uploading}
                                />
                                <input
                                    type="text"
                                    placeholder="Description"
                                    value={item.description}
                                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                    disabled={uploading}
                                />
                                <select
                                    value={item.category}
                                    onChange={(e) => updateItem(idx, 'category', e.target.value)}
                                    disabled={uploading}
                                >
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                        <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
                                    ))}
                                </select>
                                <label className="featured-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={item.featured}
                                        onChange={(e) => updateItem(idx, 'featured', e.target.checked)}
                                        disabled={uploading}
                                    />
                                    <span>Featured</span>
                                </label>
                            </div>

                            {/* Status & Actions */}
                            <div className="item-status">
                                {item.uploadStatus !== 'pending' && (
                                    <span className={`status-badge ${getStatusClass(item.uploadStatus)}`}>
                                        {getStatusText(item.uploadStatus)}
                                    </span>
                                )}
                                {item.uploadStatus === 'pending' && !uploading && (
                                    <button 
                                        className="remove-btn"
                                        onClick={() => removeItem(idx)} 
                                        disabled={uploading}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Errors Section */}
            {errors.length > 0 && (
                <div className="errors-section">
                    {errors.map((err, i) => (
                        <p key={i}>
                            <i className="fas fa-exclamation-circle"></i> {err}
                        </p>
                    ))}
                </div>
            )}

            {/* Action Buttons */}
            {mediaItems.length > 0 && (
                <div className="batch-actions">
                    <button 
                        className="btn-batch-submit"
                        onClick={handleSubmit} 
                        disabled={uploading}
                    >
                        {uploading ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Uploading & Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-upload"></i>
                                Upload & Save {mediaItems.length} Item(s)
                            </>
                        )}
                    </button>
                    <button 
                        className="btn-batch-cancel"
                        onClick={() => { setFiles([]); setMediaItems([]); setErrors([]); }} 
                        disabled={uploading}
                    >
                        <i className="fas fa-times"></i>
                        Cancel All
                    </button>
                </div>
            )}
        </div>
    );
};

export default MultiGalleryUpload;